import {
  maximumShirtNumber,
  rosterRoles,
  type FinalizeRosterRequest,
  type MatchRosterEntryView,
  type ReopenRosterRequest,
  type RosterRole,
  type RosterStatus,
  type SetRosterEntryRequest,
} from '@stm/contracts';
import type { TranslationKey } from '@stm/i18n';

export const rosterRoleKeys: Readonly<Record<RosterRole, TranslationKey>> = {
  goalkeeper: 'roster.role.goalkeeper',
  defender: 'roster.role.defender',
  forward: 'roster.role.forward',
};

export interface RosterEntryForm {
  role: RosterRole | '';
  /** The text of the shirt number field; empty means no number. */
  shirtNumber: string;
}

export const emptyRosterEntryForm: RosterEntryForm = { role: '', shirtNumber: '' };

export function rosterEntryFormOf(entry: MatchRosterEntryView): RosterEntryForm {
  return {
    role: entry.role,
    shirtNumber: entry.shirtNumber === null ? '' : String(entry.shirtNumber),
  };
}

/** The role named by a field value, or empty when it is not one of the roster roles. */
export function rosterRoleOf(text: string): RosterRole | '' {
  return rosterRoles.find((known) => known === text) ?? '';
}

/**
 * The request for the form, or null while no role is chosen or the shirt number is not a whole
 * number from 0 to 99. An empty shirt number means the athlete has none.
 */
export function rosterEntryRequest(form: RosterEntryForm): SetRosterEntryRequest | null {
  if (form.role === '') return null;
  const text = form.shirtNumber.trim();
  if (text === '') return { role: form.role, shirtNumber: null };
  if (!/^\d{1,2}$/.test(text)) return null;
  const shirtNumber = Number.parseInt(text, 10);
  return shirtNumber <= maximumShirtNumber ? { role: form.role, shirtNumber } : null;
}

/** Whether saving the form would change the entry, so the save button stays off for no change. */
export function rosterEntryChanged(form: RosterEntryForm, entry: MatchRosterEntryView): boolean {
  const request = rosterEntryRequest(form);
  return (
    request !== null && (request.role !== entry.role || request.shirtNumber !== entry.shirtNumber)
  );
}

/**
 * The finalize request for the statement typed in the form. An empty statement is left out, which
 * the server accepts while nothing on the roster still needs review.
 */
export function finalizeRosterRequest(statement: string): FinalizeRosterRequest {
  const trimmed = statement.trim();
  return trimmed === '' ? {} : { reviewAcknowledgement: trimmed };
}

/** The reopen request, or null while no reason is typed. */
export function reopenRosterRequest(reason: string): ReopenRosterRequest | null {
  const trimmed = reason.trim();
  return trimmed === '' ? null : { reason: trimmed };
}

export const rosterStatusKeys: Readonly<Record<RosterStatus, TranslationKey>> = {
  draft: 'roster.status.draft',
  finalized: 'roster.status.finalized',
};

export function rosterActionPath(
  base: string,
  matchId: string,
  action: 'finalize' | 'reopen',
): string {
  return `${rosterPath(base, matchId)}/${action}`;
}

export function rosterPath(base: string, matchId: string, athleteId?: string): string {
  const root = `${base}/matches/${encodeURIComponent(matchId)}/roster`;
  return athleteId === undefined ? root : `${root}/entries/${encodeURIComponent(athleteId)}`;
}
